import { gql } from '@/utilities/gql';
import { makeGqlMutation } from '@/utilities/gqlQuery';
import type { VariablesOf } from '@/utilities/gql';
import { produce } from 'immer';
import { useQueryClient } from '@tanstack/react-query';

import { getDefault } from './utils';
import type { GridView } from './utils';
import { useUserScreeners } from './useUserScreeners';
import { useGqlUserScreener } from './useGqlUserScreener';

const MUTATION = gql(
  `
  mutation createScreener(
        $input: CreateScreenerInput!
      ) {
        createScreener(
          input: $input
        ) {
          id
          name
          slug
          filterJson
          description
          onlyMarketFiltered
          imageUrl
          modifyDate
          status
          type {
            slug
          }
        }
      }
`
);

export type CreateScreenerVariables = VariablesOf<typeof MUTATION>;

const useCreateScreenerBase = makeGqlMutation(MUTATION);

type Options = {
  onCreate?: (payload: { id: string; slug: string | null | undefined }) => void;
  onError?: (err: unknown) => void;
};

export const useGqlCreateScreener = ({ onCreate, onError }: Options) => {
  const queryClient = useQueryClient();

  return useCreateScreenerBase({
    onSuccess: (gridViewGqlRaw) => {
      const {
        id: idInNumber,
        name,
        filterJson: rules,
        description,
        slug,
        onlyMarketFiltered,
        imageUrl,
        modifyDate,
        status,
        type,
      } = gridViewGqlRaw?.createScreener;

      const gridView = {
        id: String(idInNumber),
        rules,
        name,
        slug,
        description,
        onlyMarketFiltered,
        imageUrl,
        modifyDate,
        crawlable: !status,
        typeSlug: type?.slug,
      };

      const id = String(idInNumber);

      queryClient.setQueryData<GridView>(
        useGqlUserScreener.getAllOptions().queryKey,
        (state = getDefault()) =>
          produce(state, (draft) => {
            draft.name = name;
            draft.rules = rules;
            draft.description = description;
            draft.modifyDate = Date.now();
          })
      );

      const setDataFunc = (state: GridView[] = []) =>
        produce(state, (draft) => {
          if (draft.findIndex((n) => n.id === id) !== -1) return;
          draft.push({
            ...gridView,
            modifyDate: Date.now(),
          });
        });

      queryClient.setQueryData<GridView[]>(
        useUserScreeners.getAllOptions().queryKey,
        setDataFunc
      );

      if (typeof onCreate !== 'undefined') {
        onCreate({ id, slug });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(useUserScreeners.getAllOptions());
    },
    onError: (err) => {
      if (typeof onError !== 'undefined') {
        onError(err);
      }
    },
  });
};
